import React, { useState, useMemo } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import Wordmark from "../components/Wordmark";
// iter237 — phone input UI only. `react-phone-number-input` is the
// standard library (uses libphonenumber-js under the hood) supporting
// every ITU-recognised country, auto-dial code + flag, and emits values
// in valid E.164 format. Backend security rules stay untouched.
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/30f41ab2-b4a1-4f65-814e-99ce123f0f30/images/a7d60764e461dd70a1d1703344b38d69ea925fa140e7d762cecedddfa9a8a676.png";

export function LoginPage() {
  const { t, lang } = useI18n();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  if (user) return <Navigate to={loc.state?.from || "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await login({ email, password });
      nav(loc.state?.from || "/dashboard");
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12" data-testid="login-page">
      <div className="grid lg:grid-cols-2 max-w-5xl w-full glass rounded-3xl overflow-hidden">
        <div className="hidden lg:block relative">
          <img src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b3d91]/40 via-[#1a1a1a]/50 to-[#1a1a1a]/90" />
          <div className="relative h-full p-10 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-10 w-auto" aria-hidden />
              <Wordmark size="lg" />
            </div>
            <div>
              <div className="overline mb-3">CARTAL</div>
              <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
                {lang === "ar"
                  ? "ادخل إلى المنظومة الأكبر للسيارات في الشرق الأوسط."
                  : "Step into the Middle East's largest automotive ecosystem."}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          <div className="overline mb-2">{t("nav.login")}</div>
          <h1 className="font-display text-3xl tracking-tight font-medium mb-2">{t("auth.signInTitle")}</h1>
          <p className="text-[#6b6b6b] mb-8">{t("auth.signInSub")}</p>

          <form onSubmit={submit} className="space-y-3" data-testid="login-form">
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.email")}</label>
              <div className="relative mt-1">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b] pointer-events-none z-10" />
                <input
                  className="input-dark"
                  style={{ paddingInlineStart: "2.75rem", paddingInlineEnd: "1rem", textAlign: "start" }}
                  dir="ltr"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.password")}</label>
              <div className="relative mt-1">
                <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b] pointer-events-none z-10" />
                <input
                  className="input-dark"
                  style={{ paddingInlineStart: "2.75rem", paddingInlineEnd: "2.75rem", textAlign: "start" }}
                  dir="ltr"
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#0a0a0a]/5 transition-colors z-10"
                  aria-label={showPwd ? (lang === "ar" ? "إخفاء كلمة المرور" : "Hide password") : (lang === "ar" ? "إظهار كلمة المرور" : "Show password")}
                  title={showPwd ? (lang === "ar" ? "إخفاء" : "Hide") : (lang === "ar" ? "إظهار" : "Show")}
                  data-testid="login-password-toggle"
                >
                  {showPwd
                    ? <EyeOff className="w-4 h-4 text-[#6b6b6b]" />
                    : <Eye className="w-4 h-4 text-[#6b6b6b]" />}
                </button>
              </div>
            </div>
            {err && <div className="text-sm text-red-300" data-testid="login-error">{err}</div>}
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="login-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? t("common.signingIn") : t("common.signIn")}
            </button>
          </form>

          <p className="text-sm mt-3">
            <Link
              to="/forgot-password"
              className="text-[#8a6f2e] hover:text-[#c9a55c]"
              data-testid="forgot-password-link"
            >
              {lang === "ar" ? "نسيت كلمة المرور؟" : "Forgot password?"}
            </Link>
          </p>

          <p className="text-sm text-[#6b6b6b] mt-6">
            {t("auth.switchToRegister")}{" "}
            <Link to="/join" className="text-[#8a6f2e] hover:text-[#c9a55c]" data-testid="switch-to-register">
              {t("nav.register")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const ROLE_VALUES = [
  "individual_buyer", "individual_seller", "dealer", "agency", "showroom", "rental",
  "financing", "insurance", "inspection", "logistics", "workshop", "parts",
  "fleet", "corporate", "exporter",
];

// iter197 — split for the unified /join chooser. Individuals stay on
// their existing personal flow; every other role belongs to the
// CARTAL Business track.
// iter318 — `marine_operator` was mistakenly duplicated into
// PERSONAL_ROLES, which made "نشاط بحري (يخوت وزوارق)" show up in the
// personal signup dropdown. Marine operator is a *business* activity
// only. Individuals who own a private boat/yacht/jet-ski still publish
// their listing through `individual_seller` + `/sell?type=boat` — no
// business account required.
const PERSONAL_ROLES = ["individual_buyer", "individual_seller"];
const BUSINESS_ROLES = [
  "dealer", "agency", "showroom", "rental", "financing", "insurance",
  "inspection", "logistics", "workshop", "parts", "fleet", "corporate", "exporter",
  // iter313 — Marine operator (yachts / boats / jet-ski / marine trips).
  "marine_operator",
];

// iter313 — Marine sub-activities offered when role === "marine_operator".
// Values are stored as-is on the user document (`marine_activity_type`).
const MARINE_ACTIVITY_TYPES = [
  ["boat_rental",     "تأجير زوارق",              "Boat rental"],
  ["yacht_rental",    "تأجير يخوت",              "Yacht rental"],
  ["jet_ski_rental",  "تأجير دبابات مائية / جت سكاي", "Jet-ski rental"],
  ["fishing_trips",   "رحلات صيد",               "Fishing trips"],
  ["tourism_trips",   "رحلات ترفيهية سياحية",       "Recreational tourism trips"],
  ["diving_trips",    "رحلات غوص",               "Diving trips"],
  ["boat_sales",      "بيع زوارق ويخوت",           "Boat & yacht sales"],
  ["marine_equipment","معدات وقطع بحرية",          "Marine equipment & parts"],
  ["marine_maintenance","صيانة وميكانيكا بحرية",    "Marine maintenance & mechanics"],
  ["other",           "أخرى",                    "Other"],
];

export function RegisterPage() {
  const { t, lang } = useI18n();
  const { user, register } = useAuth();
  const [params] = useSearchParams();
  const accountType = params.get("type") === "business" ? "business"
                    : params.get("type") === "personal" ? "personal"
                    : null;

  const roleOptions = useMemo(() => {
    if (accountType === "personal") return PERSONAL_ROLES;
    if (accountType === "business") return BUSINESS_ROLES;
    return ROLE_VALUES;
  }, [accountType]);
  // iter313 — When arriving with ?activity=marine (from the /marine
  // partner CTA), pre-select the marine_operator role so users don't
  // have to hunt for it in the dropdown.
  const activityParam = params.get("activity");
  const preselectMarine = activityParam === "marine";
  const defaultRole = preselectMarine
    ? "marine_operator"
    : (accountType === "business" ? BUSINESS_ROLES[0] : "individual_buyer");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: defaultRole,
    company_name: "",
    marine_activity_type: preselectMarine ? MARINE_ACTIVITY_TYPES[0][0] : "",
  });
  // iter237 — track selected country to swap the SA-specific placeholder
  // for the library's country-aware default when a non-SA country is picked.
  const [phoneCountry, setPhoneCountry] = useState("SA");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  // iter259 — GLOBAL AUTH GATE. After hooks (rules-of-hooks safe).
  // Every guest who lands here without first passing through the
  // unified "انضم إلى كارتال" chooser is bounced there so the user
  // (not the CTA) picks Personal vs Business. `via=join` is set by
  // JoinChooserPage right before it navigates here. Every relevant
  // query param is carried through so we return the user to their
  // original destination after registration.
  if (params.get("via") !== "join") {
    const pass = new URLSearchParams();
    const next = params.get("next");
    const intent = params.get("intent");
    const activity = params.get("activity");
    const type = params.get("type");
    if (next) pass.set("next", next);
    if (intent) pass.set("intent", intent);
    if (activity) pass.set("activity", activity);
    if (type) pass.set("suggested_type", type);
    return <Navigate to={`/join${pass.toString() ? `?${pass}` : ""}`} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // iter313 — Marine sub-activity only travels when relevant.
      const payload = { ...form };
      if (payload.role !== "marine_operator") delete payload.marine_activity_type;
      await register(payload);
      // iter241 — flag brand-new signups so the post-registration
      // city-onboarding modal (mounted at the app root) fires ONCE
      // for this account. Cleared on save or dismiss.
      try { localStorage.setItem("cartal-city-onboard-pending", "1"); } catch { /* ignore */ }
      // iter255 — if the account was created via a partner intent
      // (came from "سجّل كشريك معتمد" on any page), send them straight
      // to the business-creation form so the CTA actually leads to
      // "تسجيل نشاط تجاري" without a detour through the dashboard.
      const partnerIntent = params.get("intent") === "partner";
      const next = params.get("next");
      if (next) {
        // iter259 — restore user's original destination (deep link, ad
        // page, message CTA, …) that triggered the auth gate.
        nav(next);
      } else if (partnerIntent) {
        const activity = params.get("activity");
        nav(`/businesses/new${activity ? `?activity=${encodeURIComponent(activity)}` : ""}`);
      } else {
        nav("/dashboard");
      }
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="grid lg:grid-cols-[1fr_1.1fr] max-w-5xl w-full glass rounded-3xl overflow-hidden">
        <div className="hidden lg:block relative">
          <img src="https://images.pexels.com/photos/16176576/pexels-photo-16176576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b3d91]/40 via-[#1a1a1a]/55 to-[#1a1a1a]/90" />
          <div className="relative h-full p-10 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="" className="h-10 w-auto" aria-hidden />
              <Wordmark size="lg" />
            </div>
            <div>
              <div className="overline mb-3">JOIN</div>
              <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
                {lang === "ar"
                  ? "حساب واحد. كل خدمات المركبات."
                  : "One account. Every vehicle service."}
              </h2>
            </div>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          {accountType && (
            <div className="mb-3 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full"
                 style={accountType === "business"
                   ? { backgroundColor: "#0a0a0a", color: "#e8c873", border: "1px solid rgba(232,200,115,0.55)" }
                   : { backgroundColor: "#fdf9ef", color: "#8a6f2e", border: "1px solid rgba(201,165,92,0.3)" }}
                 data-testid={`register-track-${accountType}`}>
              {accountType === "business"
                ? (<>CARTAL <span style={{ color: "#e8c873" }}>Business</span></>)
                : (lang === "ar" ? "حساب شخصي" : "Personal account")}
              <Link to="/join" className="opacity-70 hover:opacity-100 underline underline-offset-2" data-testid="register-change-track">
                {lang === "ar" ? "تغيير" : "change"}
              </Link>
            </div>
          )}
          <div className="overline mb-2">{t("nav.register")}</div>
          <h1 className="font-display text-3xl tracking-tight font-medium mb-2">{t("auth.registerTitle")}</h1>
          <p className="text-[#6b6b6b] mb-6">{t("auth.registerSub")}</p>

          <form onSubmit={submit} className="space-y-3" data-testid="register-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6b6b6b]">{t("common.name")}</label>
                <input className="input-dark mt-1" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="register-name" />
              </div>
              <div>
                <label className="text-xs text-[#6b6b6b]">{t("common.phone")}</label>
                {/* iter237 — country selector + local number → E.164 on the fly.
                    Default country is Saudi Arabia (SA) so the placeholder
                    reads "5XXXXXXXX" out of the box per Support prompt;
                    users can pick any country from the built-in list (flag
                    + auto dial code). Placeholder is only fixed for SA;
                    for other countries the library's country-aware default
                    kicks in. PhoneInput emits E.164 (e.g. +9665XXXXXXXX)
                    directly into form.phone. Visual container mirrors
                    input-dark so the row keeps the existing form style. */}
                <div
                  className="input-dark mt-1 flex items-center gap-2 px-3 py-2 cartal-phone-wrap"
                  data-testid="register-phone-wrap"
                >
                  <PhoneInput
                    international
                    defaultCountry="SA"
                    countryCallingCodeEditable={false}
                    placeholder={phoneCountry === "SA" ? "5XXXXXXXX" : undefined}
                    value={form.phone || ""}
                    onChange={(val) => setForm({ ...form, phone: val || "" })}
                    onCountryChange={(c) => setPhoneCountry(c || "SA")}
                    className="cartal-phone flex-1"
                    numberInputProps={{
                      "data-testid": "register-phone",
                      "aria-label": t("common.phone"),
                      autoComplete: "tel",
                    }}
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.email")}</label>
              <input className="input-dark mt-1" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="register-email" />
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.password")}</label>
              <div className="relative mt-1">
                <input
                  className="input-dark pe-10"
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({...form, password: e.target.value})}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute end-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center hover:bg-[#0a0a0a]/5 transition-colors"
                  aria-label={showPwd ? (lang === "ar" ? "إخفاء كلمة المرور" : "Hide password") : (lang === "ar" ? "إظهار كلمة المرور" : "Show password")}
                  data-testid="register-password-toggle"
                >
                  {showPwd
                    ? <EyeOff className="w-4 h-4 text-[#6b6b6b]" />
                    : <Eye className="w-4 h-4 text-[#6b6b6b]" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6b6b6b]">{t("common.role")}</label>
              <select className="input-dark mt-1" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} data-testid="register-role">
                {roleOptions.map((r) => <option key={r} value={r}>{t(`auth.roles.${r}`)}</option>)}
              </select>
            </div>
            {/* iter313 — Marine sub-activity picker. Only visible when the
                user selected the marine operator role. Values map 1:1 to
                the backend `marine_activity_type` field. */}
            {form.role === "marine_operator" && (
              <div data-testid="register-marine-activity-block">
                <label className="text-xs text-[#6b6b6b]">
                  {lang === "ar" ? "نوع النشاط البحري" : "Marine activity type"}
                  <span className="text-red-400"> *</span>
                </label>
                <select
                  className="input-dark mt-1"
                  required
                  value={form.marine_activity_type}
                  onChange={(e) => setForm({ ...form, marine_activity_type: e.target.value })}
                  data-testid="register-marine-activity"
                >
                  <option value="" disabled>
                    {lang === "ar" ? "اختر نوع النشاط" : "Select an activity"}
                  </option>
                  {MARINE_ACTIVITY_TYPES.map(([key, ar, en]) => (
                    <option key={key} value={key}>{lang === "ar" ? ar : en}</option>
                  ))}
                </select>
                <div className="text-[11px] text-[#8a6f2e] mt-1 leading-relaxed">
                  {lang === "ar"
                    ? "الأنشطة القابلة للتأجير تدعم لاحقًا كلا التعاملَين: بيع أو تأجير عند نشر الإعلان."
                    : "Rentable marine activities will support both sale and rental listings when posting an ad."}
                </div>
              </div>
            )}
            {["dealer","agency","showroom","rental","financing","insurance","inspection","logistics","workshop","parts","fleet","corporate","exporter","marine_operator"].includes(form.role) && (
              <div>
                <label className="text-xs text-[#6b6b6b]">
                  {lang === "ar" ? "اسم النشاط التجاري" : "Business (trading) name"}
                  <span className="text-red-400"> *</span>
                </label>
                <input
                  className="input-dark mt-1"
                  required={accountType === "business"}
                  value={form.company_name}
                  onChange={(e) => setForm({...form, company_name: e.target.value})}
                  placeholder={lang === "ar" ? "مثال: معرض الرياض" : "e.g. Riyadh Motors"}
                  data-testid="register-company"
                />
                {accountType === "business" && (
                  <div className="text-[11px] text-[#8a6f2e] mt-1 leading-relaxed" data-testid="register-company-hint">
                    {lang === "ar"
                      ? "سيتم إنشاء هذا النشاط داخل كارتال أعمال ← مساحاتي مباشرة، ومنه تبدأ خطوات التوثيق التجاري."
                      : "This activity will be created inside CARTAL Business → My Spaces, and business verification starts from it."}
                  </div>
                )}
              </div>
            )}
            {err && <div className="text-sm text-red-300" data-testid="register-error">{err}</div>}
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="register-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {busy ? t("common.creating") : t("common.createAccount")}
            </button>
          </form>

          <p className="text-xs text-[#6b6b6b] mt-5 leading-relaxed">
            {t("common.legal")}
          </p>

          <p className="text-sm text-[#6b6b6b] mt-4">
            {t("auth.switchToLogin")}{" "}
            <Link to="/login" className="text-[#8a6f2e] hover:text-[#c9a55c]" data-testid="switch-to-login">
              {t("nav.login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
